import React, { useState, useCallback, useEffect } from "react";
import { ScrollRestoration, useNavigate, useParams } from "react-router-dom";
import CollectionFiltering from "./components/CollectionFiltering";
import BrandVideos from "./components/BrandVideos";
import InsightsModal from "./components/InsightsModal";
import Modal from "@/components/ui/Modal";
import toast from "react-hot-toast";
import { ArrowLeft, Package } from "lucide-react";
import ManualCompetitorModal from "./components/ManualCompetitorModal";
import { CustomButton } from "@/components/ui/Buttons";
import { useGetCompetitorVideosQuery } from "@/redux/feature/product/productApi";
import CompetitorVideos from "./components/CompetitorVideos/CompetitorVideos2";
import { usePagination } from "@/hooks/usePagination";

const ProductCollections = () => {
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [selectedAudience, setSelectedAudience] = useState<string | null>(null);

  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"competitor" | "brand">(
    "competitor"
  );
  const [isManualCompetitorModalOpen, setIsManualCompetitorModalOpen] =
    useState(false);

  const competitorPagination = usePagination();

  const { brandName, productName, country, productId } = useParams();
  const navigate = useNavigate();

  const handleBrandChange = (brand: string | null) => {
    setSelectedBrand(brand);
  };

  const handleAudienceChange = (audience: string | null) => {
    setSelectedAudience(audience);
  };

  // navigate to the product structure page when the audience is selected
  const handlerNavigateStructure = () => {
    console.log(selectedAudience);

    if (!selectedAudience) {
      toast.error("You need to select the audience");
    } else {
      navigate(
        `/product-structure/${productId}/${brandName}/${productName}/${selectedAudience}/${country}`
      );
    }
  };

  // open the insights modal
  const handlerOpenInsightsModal = () => {
    setIsInsightsModalOpen(true);
    console.log(isInsightsModalOpen);
  };

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // competitor videos api
  const [pollingStatus, setPollingStatus] = useState({
    competitor: true,
  });
  const POLLING_INTERVAL = 10000;

  const {
    data: competitorResponse = { data: [], meta: { isProcessing: false } },
    isFetching: isCompetitorFetching,
    isLoading: isCompetitorLoading,
  } = useGetCompetitorVideosQuery(
    {
      productId: productId!,
      page: competitorPagination.page,
      limit: competitorPagination.limit,
    },
    {
      pollingInterval: pollingStatus.competitor ? POLLING_INTERVAL : 0,
      skip: !!selectedAudience || !!selectedBrand,
    }
  );

  // effect for stop pooling
  useEffect(() => {
    setPollingStatus((prev) => ({
      ...prev,

      competitor: competitorResponse.meta?.isProcessing !== false,
    }));
  }, [competitorResponse.meta]);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        {/* Back button */}
        <div className="mb-4 sm:mb-6">
          <CustomButton
            variant="outline"
            fontSize="text-xs sm:text-sm"
            fontWeight="500"
            color="#0D0D0D"
            border="1px solid #E4E4E4"
            borderRadius="30px"
            onClick={handleGoBack}
            className="flex items-center gap-x-2 sm:gap-x-2.5 px-3 py-2 sm:px-4 leading-5 sm:leading-6"
          >
            <ArrowLeft size={18} className="sm:size-6" />
            <span className="hidden sm:inline">Go back to product list</span>
            <span className="sm:hidden">Go back</span>
          </CustomButton>
        </div>

        {/* Product header */}
        <div className="pb-4 sm:pb-6 border-b border-[#6B6B6B4D] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-x-3">
            <div className="bg-[#00000033] size-12 sm:size-[66px] rounded-full flex items-center justify-center flex-shrink-0">
              <Package className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <h1 className="text-[#0D0D0D] text-xl sm:text-2xl lg:text-3xl font-medium leading-tight capitalize">
              {productName}
            </h1>
          </div>

          {/* Action buttons  */}
          <div className="flex flex-col md:flex-row flex-wrap justify-center gap-3 w-full md:w-fit">
            <CustomButton
              backgroundColor="#000000"
              padding="12px"
              color="white"
              fontSize="15px"
              fontWeight="500"
              borderRadius="8px"
              onClick={handlerNavigateStructure}
              disabled={pollingStatus.competitor}
              className="leading-normal whitespace-nowrap w-full md:w-fit"
            >
              View recommended structure
            </CustomButton>
            <CustomButton
              variant="outline"
              backgroundColor="#000000"
              padding="12px"
              color="#000000"
              fontSize="15px"
              fontWeight="500"
              border="1px solid #000"
              borderRadius="8px"
              className="leading-normal whitespace-nowrap w-full md:w-fit"
              onClick={handlerOpenInsightsModal}
            >
              View insights
            </CustomButton>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col lg:flex-row lg:gap-8 space-y-6 lg:space-y-0">
        {/* Left sidebar - CollectionFiltering */}
        <aside className="w-full lg:w-[220px] 2xl:w-[320px] order-1 lg:order-1">
          <div className="lg:sticky lg:top-4">
            <CollectionFiltering
              handlerNavigateStructure={handlerNavigateStructure}
              handlerOpenInsightsModal={handlerOpenInsightsModal}
              onBrandChange={handleBrandChange}
              setIsManualCompetitorModalOpen={setIsManualCompetitorModalOpen}
            />
          </div>
        </aside>

        {/* Main content - CompetitorVideos */}
        <main className="flex-1 min-w-0 order-3 lg:order-2">
          <CompetitorVideos
            selectedAudience={selectedAudience}
            selectedBrand={selectedBrand}
            onAudienceChange={handleAudienceChange}
            setSelectedAudience={setSelectedAudience}
            setSelectedBrand={setSelectedBrand}
          />
        </main>

        {/* Right sidebar - BrandVideos */}
        <aside className="w-full lg:w-[220px] 2xl:w-[320px] order-2 lg:order-3">
          <div className="lg:sticky lg:top-4">
            <BrandVideos
              handlerNavigateStructure={handlerNavigateStructure}
              handlerOpenInsightsModal={handlerOpenInsightsModal}
              isBrandModalOpen={isBrandModalOpen}
              isInsightsModalOpen={isInsightsModalOpen}
              setIsInsightsModalOpen={setIsInsightsModalOpen}
              setIsBrandModalOpen={setIsBrandModalOpen}
              selectedAudience={selectedAudience}
            />
          </div>
        </aside>
      </div>

      {/* Modals */}
      <Modal
        isOpen={isInsightsModalOpen}
        setOpen={setIsInsightsModalOpen}
        width="800px"
      >
        <InsightsModal
          onSelectPersona={handleAudienceChange}
          setIsInsightsModalOpen={setIsInsightsModalOpen}
        />
      </Modal>

      <Modal
        isOpen={isManualCompetitorModalOpen}
        setOpen={setIsManualCompetitorModalOpen}
        width="600px"
      >
        <ManualCompetitorModal setIsOpen={setIsManualCompetitorModalOpen} />
      </Modal>

      <ScrollRestoration />
    </div>
  );
};

export default ProductCollections;
